// 騎士の突き (lanceThrust)・薙ぎ (lanceSweep) の足を床に留める版 (2026-10-28・介錯の突き当て 1028e の作りを持ち込む)。
//   新しい絵は足していない。絵の替わり目のずらし (offset)・溜めの間の動き・膨らみと震えだけを変えた (手順1＝時間の割り振りだけの比較版)。
//   秒数・判定・当たる絵 (lanceThrust_full / lanceSweep_contact) の位置は配布版のまま＝描画だけ。
//   10-30 作者「この前の版であれば、おかしいところはありませんでした」（1028 は溜めで反る・当たりで跳ぶ・ちらつく）＝配布版の既定は old。開発版の ?knightSmooth=1028 で比べられる。
//   計画 = docs/research/2026-10-28-smooth-frame-plan-hound-knight-shade.md・記録 = docs/research/2026-10-28-knight-thrust-sweep-smooth.md
//
// 配布版を毎コマ撮って両足の位置を測った (scripts/capture_knight_smooth_1028.mjs・画面 px・左向き＝小さい方が前):
//   突き 待機 [460.9, 485.9] → 槍を下ろす絵 [473.3, 544.9] → 構え [462.7, 569.7] → 打つ直前 [473.5, 603.6]
//     → 当たる絵 (体が 82px 前へ出た後) [408.4, 546] → 伸び切り [370.6, 509.5] → 戻り [388.9, 487.8]
//   絵が替わるたびに前足も後ろ足も 10〜38px 跳んでいた。
//   直し方: 前足を床に留め、後ろ足だけが絵の替わり目で動く (下がって構え、戻りで寄せる)。ずらしは前足の差から逆算した。
//     伸び切りは前後とも 37px 前へ跳んでいた (同じ構えを前へずらして置いた絵) ので、当たる絵の足の所へ戻す。
//   当たる絵のずらしは 0 のまま (当たりは体の位置から測る)。踏み込みの 3 コマだけは体ごと動くので足は留めない。
//   足を留めるキーは ease 'step' (次のキーのずらしへ補間しない)。
import { BOSS_ATTACK_TIMELINES, KNIGHT_SWEEP_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { setKnightSmoothFlags } from './knight_smooth_flags_1028'

// 構えの間も止めない: 前足を軸にわずかに沈み、後ろへ反る (ゆっくり始めて終わりへ向けて深く・介錯の溜めと同じ考え)。
const SINK = { y: 4 }
const LEAN = -1.5

const THRUST_ACTIVE_RECOVERY: BossAttackTimeline = [
  { phase: 'active', at: 0.0, image: 'lanceThrust_full', tag: 'contact', ease: 'step' },
  // 伸び切り: 前足を当たる絵の前足の所に留める。前へ少しずつ傾いて、勢いが抜けていく (前傾は足元を軸に回す)。
  { phase: 'recovery', at: 0.0, image: 'lanceThrust_overextend', hold: true, ease: 'easeOut', offset: { x: -37.8 } },
  { phase: 'recovery', at: 0.5, image: 'lanceThrust_overextend', hold: true, ease: 'step', offset: { x: -37.8 }, rot: 1.5 },
  // 戻り: 前足を留め、後ろ足を 40px 寄せる。
  { phase: 'recovery', at: 0.55, image: 'lanceThrust_recover', tag: 'followThrough', ease: 'step', offset: { x: -19.5 } },
]

export const KNIGHT_THRUST_TIMELINE_1028: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'idle', ease: 'step' },
  // 槍を下ろす絵: 前足を待機の前足に留め、後ろ足を 47px 引く。
  { phase: 'telegraph', at: 0.25, image: 'lanceThrust_coilStart', hold: true, tag: 'anticipation', ease: 'step', offset: { x: 12.4 } },
  // 構え: 前足を留め、後ろ足をさらに 35px 引く。構えの間は沈んで反り続ける。
  { phase: 'telegraph', at: 0.4, image: 'lanceThrust_windup', hold: true, tag: 'anticipation', ease: 'easeIn', offset: { x: 1.8 } },
  { phase: 'telegraph', at: 0.84, image: 'lanceThrust_windup', hold: true, tag: 'anticipation', ease: 'step', offset: { x: 1.8, ...SINK }, rot: LEAN },
  // 打つ直前: 前足を留め、後ろ足を 23px 引く。
  { phase: 'telegraph', at: 0.85, image: 'lanceThrust_strike', hold: true, ease: 'step', offset: { x: 12.6 } },
  ...THRUST_ACTIVE_RECOVERY,
]

// 連の2拍目以降の突き。構え (lanceThrust_linkWindup) から入り、打つ直前の絵で前足を留める (連の撮影で前足の差 11.7px)。
export const KNIGHT_THRUST_LINK_TIMELINE_1028: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'lanceThrust_windup', hold: true, tag: 'anticipation', ease: 'easeIn' },
  { phase: 'telegraph', at: 0.84, image: 'lanceThrust_windup', hold: true, tag: 'anticipation', ease: 'step', offset: SINK, rot: LEAN },
  { phase: 'telegraph', at: 0.85, image: 'lanceThrust_strike', hold: true, ease: 'step', offset: { x: 11.7 } },
  ...THRUST_ACTIVE_RECOVERY,
]

// 薙ぎ: 待機 [400.9, 425.9] → 構え [377, 498.4] で前足が 24px 前へ・後ろ足が 72px 後ろへ跳んでいた。
//   前足を留め、後ろ足だけを引く。構えの間は沈んで反り続ける。振りの途中に絵は足さない (09-27 作者「キーポーズだけ作って間はエフェクトなどで」)。
//   振り終わりから後ろ (振り終わり・構え直し・足を寄せる3段) は 10-27 に足の跳びを割ったので変えない。
const SWEEP_ACTIVE_RECOVERY: BossAttackTimeline = [
  { phase: 'active', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'contact', ease: 'step' },
  { phase: 'recovery', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'followThrough', ease: 'step' },
]
export const KNIGHT_SWEEP_TIMELINE_1028: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'idle', ease: 'step' },
  { phase: 'telegraph', at: 0.45, image: 'lanceSweep_windup', hold: true, tag: 'anticipation', ease: 'easeIn', offset: { x: -23.9 } },
  { phase: 'telegraph', at: 0.97, image: 'lanceSweep_windup', hold: true, tag: 'anticipation', ease: 'step', offset: { x: -23.9, y: 3 }, rot: -2 },
  ...SWEEP_ACTIVE_RECOVERY,
]
export const KNIGHT_SWEEP_LINK_TIMELINE_1028: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'lanceSweep_windup', hold: true, tag: 'anticipation', ease: 'easeIn' },
  { phase: 'telegraph', at: 0.97, image: 'lanceSweep_windup', hold: true, tag: 'anticipation', ease: 'step', offset: { y: 3 }, rot: -2 },
  ...SWEEP_ACTIVE_RECOVERY,
]

// 突きの後の足を寄せる絵 (rest_from_thrust) は戻りの絵より 43px 後ろに置かれ、待機へ替わる1コマで前足が 72px 跳んでいた。
//   戻りの前足 (408.4) と待機の後ろ足 (404.1) の所 (403.8) へ置き直す (原画 px・sprites.ts の KNIGHT_REST_ANCHOR_SHIFT_PX の上書き)。
//   左向きでは原画 px を増やすと画面で前へ出る (撮影で 122 原画 px ≒ 22 画面 px)。
const REST_SHIFT_1028: Partial<Record<string, number>> = { rest_from_thrust: 360 }

// ?knightSmooth=rest (10-30・開発版だけ): old の並びのまま、足を寄せる絵の置き直し (REST_SHIFT_1028) だけを当てる。
//   old を既定で撮ると 戻り→足を寄せる絵 で前足 +62px、足を寄せる絵→待機 で -72px 跳んでいた (docs/research/2026-10-30-knight-rest-jump-check.md)。
type KnightSmoothMode = 'old' | '1028' | 'rest'
let mode: KnightSmoothMode = 'old'
export function knightSmoothMode(): KnightSmoothMode { return mode }
const ORIGINAL = { ...BOSS_ATTACK_TIMELINES['vigil-knight']! }
export function setKnightSmooth(raw: string | null): void {
  mode = import.meta.env.DEV && (raw === '1028' || raw === 'rest') ? raw : 'old'
  const on = mode === '1028'
  setKnightSmoothFlags(on, on ? { lanceThrust: KNIGHT_THRUST_LINK_TIMELINE_1028, lanceSweep: KNIGHT_SWEEP_LINK_TIMELINE_1028 } : {}, mode === 'old' ? {} : REST_SHIFT_1028)
  const tls = BOSS_ATTACK_TIMELINES['vigil-knight']!
  if (on) {
    tls.lanceThrust = KNIGHT_THRUST_TIMELINE_1028
    // 薙ぎは配布版の並び (keypose) の時だけ替える (?sweepWeight= や ?knightSweep=0912 の比較版は残す)。
    if (tls.lanceSweep === KNIGHT_SWEEP_TIMELINES.keypose) tls.lanceSweep = KNIGHT_SWEEP_TIMELINE_1028
  } else {
    if (tls.lanceThrust === KNIGHT_THRUST_TIMELINE_1028) tls.lanceThrust = ORIGINAL.lanceThrust!
    if (tls.lanceSweep === KNIGHT_SWEEP_TIMELINE_1028) tls.lanceSweep = KNIGHT_SWEEP_TIMELINES.keypose
  }
}
// 読み込んだ時点で配布版の並びにする (game.ts を通らないラボ・テストも同じ並びを見る)。
setKnightSmooth(null)
